// ============================================================
// FILE: CreditLedger.js
// LOCATION: C:\gardenmarket\web\src\components\CreditLedger.js
// ============================================================
// PURPOSE:
//   Full credit statement for a gardener — paged history, date
//   and type filters, and CSV export.
//
// WHY THIS EXISTS ALONGSIDE CreditBalance:
//   CreditBalance shows the balance plus the last 20 transactions
//   as a dashboard widget. That is a snapshot, not a statement:
//   no way to see older activity, no date range, and a fee row
//   does not say which job it was for. Gardeners are being
//   charged real money, so they need to be able to reconcile
//   every line.
//
// CSV EXPORT:
//   Requests page_size=0 (all rows) and builds the file in the
//   browser. Rows are reversed to chronological order, because
//   that is how a statement reads and how accounting software
//   expects to import it.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import client from '../api/client';
import { colors } from '../theme/theme';

const PAGE_SIZE = 25;

const TYPE_LABELS = {
    topup:    '+ Top up',
    bonus:    'Bonus',
    job_fee:  '- Job fee',
    refund:   '+ Refund',
    // No leading sign: a referral row is positive when the bonus
    // pays and negative when it is clawed back after a refund.
    referral: 'Referral bonus',
};

const TYPE_FILTERS = [
    { value: '',         label: 'All activity' },
    { value: 'job_fee',  label: 'Job fees' },
    { value: 'topup',    label: 'Top ups' },
    { value: 'referral', label: 'Referral bonuses' },
    { value: 'bonus',    label: 'Bonuses' },
    { value: 'refund',   label: 'Refunds' },
];

function fmt(amount, currency = 'AUD') {
    const n = Math.abs(Number(amount) || 0);
    const symbol = currency === 'USD' ? 'US$' : currency === 'NZD' ? 'NZ$' : '$';
    return `${symbol}${n.toFixed(2)}`;
}

function fmtDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined,
        { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function CreditLedger() {
    const [rows,      setRows]      = useState([]);
    const [total,     setTotal]     = useState(0);
    const [balance,   setBalance]   = useState(null);
    const [period,    setPeriod]    = useState(null);
    const [page,      setPage]      = useState(1);
    const [txnType,   setTxnType]   = useState('');
    const [fromDate,  setFromDate]  = useState('');
    const [toDate,    setToDate]    = useState('');
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState('');
    const [exporting, setExporting] = useState(false);

    const currency = balance?.currency || 'AUD';

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await client.get('/credits/ledger', {
                params: {
                    page,
                    page_size: PAGE_SIZE,
                    txn_type:  txnType || undefined,
                    from:      fromDate || undefined,
                    to:        toDate   || undefined,
                },
            });
            setRows(r.data.transactions || []);
            setTotal(r.data.total_rows || 0);
            setBalance(r.data.balance);
            setPeriod(r.data.period);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not load your statement.');
        } finally {
            setLoading(false);
        }
    }, [page, txnType, fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    // Any filter change resets to page 1 — otherwise you can end up
    // on page 4 of a 2-page result and see nothing.
    const changeFilter = (setter) => (value) => {
        setter(value);
        setPage(1);
    };

    const exportCsv = async () => {
        setExporting(true);
        try {
            const r = await client.get('/credits/ledger', {
                params: {
                    page_size: 0,                     // all rows
                    txn_type:  txnType || undefined,
                    from:      fromDate || undefined,
                    to:        toDate   || undefined,
                },
            });

            const all = (r.data.transactions || []).slice().reverse();  // chronological

            const esc = (v) => {
                const s = v === null || v === undefined ? '' : String(v);
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };

            const header = ['Date','Type','Description','Job','Job ID',
                            'Amount','Balance after','Currency','Payment ref'];

            const lines = all.map(t => [
                new Date(t.created_at).toISOString().slice(0, 10),
                TYPE_LABELS[t.txn_type] || t.txn_type,
                t.description,
                t.job_title || '',
                t.job_id || '',
                Number(t.amount).toFixed(2),
                Number(t.balance_after).toFixed(2),
                t.currency,
                t.payment_ref || '',
            ].map(esc).join(','));

            const csv  = [header.join(','), ...lines].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = `gardenhand-statement-${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError('Could not export. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasFilters = txnType || fromDate || toDate;

    return (
        <div style={styles.wrap}>
            <div style={styles.headRow}>
                <h3 style={styles.title}>Statement</h3>
                <button
                    type="button"
                    onClick={exportCsv}
                    disabled={exporting || total === 0}
                    style={styles.csvBtn}
                    className="gh-touch-target"
                >
                    {exporting ? 'Exporting...' : 'Download CSV'}
                </button>
            </div>

            {/* Period summary — recalculates with the filters */}
            {period && period.txn_count > 0 && (
                <div className="gh-grid-4" style={styles.summary}>
                    <Stat label="Top ups"  value={fmt(period.topups, currency)} />
                    <Stat label="Bonuses"  value={fmt(period.bonuses, currency)} />
                    <Stat label="Fees"     value={fmt(period.fees_charged, currency)} negative />
                    <Stat label="Jobs billed" value={period.jobs_charged ?? 0} />
                </div>
            )}

            {/* Filters */}
            <div style={styles.filters}>
                <select
                    value={txnType}
                    onChange={e => changeFilter(setTxnType)(e.target.value)}
                    style={styles.select}
                >
                    {TYPE_FILTERS.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                </select>
                <input
                    type="date" value={fromDate}
                    onChange={e => changeFilter(setFromDate)(e.target.value)}
                    style={styles.date} aria-label="From date"
                />
                <span style={styles.dateSep}>to</span>
                <input
                    type="date" value={toDate}
                    onChange={e => changeFilter(setToDate)(e.target.value)}
                    style={styles.date} aria-label="To date"
                />
                {hasFilters && (
                    <button
                        type="button"
                        onClick={() => { setTxnType(''); setFromDate(''); setToDate(''); setPage(1); }}
                        style={styles.clearBtn}
                    >
                        Clear
                    </button>
                )}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {loading && <p style={styles.muted}>Loading...</p>}

            {!loading && rows.length === 0 && (
                <p style={styles.muted}>
                    {hasFilters
                        ? 'No activity matches those filters.'
                        : 'No credit activity yet.'}
                </p>
            )}

            {!loading && rows.map(t => {
                const credit = Number(t.amount) > 0;
                return (
                    <div key={t.txn_id} style={styles.row}>
                        <div style={styles.rowLeft}>
                            <span style={credit ? styles.pillCredit : styles.pillDebit}>
                                {TYPE_LABELS[t.txn_type] || t.txn_type}
                            </span>
                            <div style={styles.desc}>
                                {/* The job a fee relates to, so the charge can be
                                    reconciled. Links through to the booking. */}
                                {t.job_id && t.job_title ? (
                                    <Link to={`/gardener/booking/${t.job_id}`} style={styles.jobLink}>
                                        {t.job_title}
                                    </Link>
                                ) : (
                                    <span>{t.description}</span>
                                )}
                                {t.job_suburb && (
                                    <span style={styles.suburb}> · {t.job_suburb}</span>
                                )}
                                {t.payment_ref && (
                                    <span style={styles.ref}> · ref {t.payment_ref}</span>
                                )}
                            </div>
                        </div>
                        <div style={styles.rowRight}>
                            <span style={credit ? styles.amtCredit : styles.amtDebit}>
                                {credit ? '+' : '-'}{fmt(t.amount, t.currency || currency)}
                            </span>
                            <span style={styles.after}>
                                bal {fmt(t.balance_after, t.currency || currency)}
                            </span>
                            <span style={styles.when}>{fmtDate(t.created_at)}</span>
                        </div>
                    </div>
                );
            })}

            {/* Paging */}
            {totalPages > 1 && (
                <div style={styles.pager}>
                    <button
                        type="button" onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1} style={styles.pageBtn}
                        className="gh-touch-target"
                    >
                        ← Newer
                    </button>
                    <span style={styles.pageInfo}>
                        Page {page} of {totalPages} · {total} entries
                    </span>
                    <button
                        type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages} style={styles.pageBtn}
                        className="gh-touch-target"
                    >
                        Older →
                    </button>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, negative }) {
    return (
        <div style={styles.stat}>
            <div style={negative ? styles.statValNeg : styles.statVal}>{value}</div>
            <div style={styles.statLabel}>{label}</div>
        </div>
    );
}

const pill = {
    display:'inline-block', fontSize:'0.72rem', fontWeight:700,
    padding:'2px 8px', borderRadius:'999px', marginRight:'8px', flexShrink:0,
};

const styles = {
    wrap:        { background:'white', borderRadius:'10px', padding:'20px',
                   boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginTop:'16px' },
    headRow:     { display:'flex', justifyContent:'space-between', alignItems:'center',
                   gap:'12px', marginBottom:'14px', flexWrap:'wrap' },
    title:       { margin:0, fontSize:'1rem', fontWeight:'bold', color:colors.brand },
    csvBtn:      { padding:'7px 14px', border:`1px solid ${colors.border}`, background:'white',
                   borderRadius:'6px', fontSize:'0.82rem', cursor:'pointer', color:colors.brand,
                   fontWeight:600 },

    summary:     { marginBottom:'16px', paddingBottom:'14px',
                   borderBottom:`1px solid ${colors.border}` },
    stat:        { textAlign:'center' },
    statVal:     { fontSize:'1rem', fontWeight:700, color:colors.text },
    statValNeg:  { fontSize:'1rem', fontWeight:700, color:'#991B1B' },
    statLabel:   { fontSize:'0.72rem', color:colors.textMuted, marginTop:'2px' },

    filters:     { display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap',
                   marginBottom:'14px' },
    // 16px font prevents iOS Safari zooming on focus
    select:      { padding:'7px 10px', border:`1px solid ${colors.border}`,
                   borderRadius:'6px', fontSize:'16px', background:'white' },
    date:        { padding:'7px 10px', border:`1px solid ${colors.border}`,
                   borderRadius:'6px', fontSize:'16px' },
    dateSep:     { fontSize:'0.8rem', color:colors.textMuted },
    clearBtn:    { padding:'7px 12px', border:'none', background:'none',
                   color:colors.brand, fontSize:'0.82rem', cursor:'pointer',
                   textDecoration:'underline' },

    row:         { display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                   gap:'10px', padding:'11px 0', borderBottom:`1px solid ${colors.border}` },
    rowLeft:     { display:'flex', alignItems:'flex-start', minWidth:0, flex:1 },
    rowRight:    { display:'flex', flexDirection:'column', alignItems:'flex-end',
                   gap:'2px', flexShrink:0 },
    desc:        { fontSize:'0.85rem', color:colors.textMuted, minWidth:0 },
    jobLink:     { color:colors.brand, fontWeight:600, textDecoration:'none' },
    suburb:      { color:colors.textFaint },
    ref:         { color:colors.textFaint, fontSize:'0.78rem' },
    pillCredit:  { ...pill, backgroundColor:'#DCFCE7', color:'#14532D' },
    pillDebit:   { ...pill, backgroundColor:'#FEE2E2', color:'#991B1B' },
    amtCredit:   { fontWeight:700, color:'#14532D' },
    amtDebit:    { fontWeight:700, color:'#991B1B' },
    after:       { fontSize:'0.72rem', color:colors.textMuted },
    when:        { fontSize:'0.72rem', color:colors.textFaint },

    pager:       { display:'flex', justifyContent:'space-between', alignItems:'center',
                   gap:'10px', marginTop:'14px', flexWrap:'wrap' },
    pageBtn:     { padding:'7px 14px', border:`1px solid ${colors.border}`, background:'white',
                   borderRadius:'6px', fontSize:'0.85rem', cursor:'pointer', color:colors.brand },
    pageInfo:    { fontSize:'0.78rem', color:colors.textMuted },

    error:       { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:'6px',
                   padding:'10px 14px', color:'#c0392b', marginBottom:'12px', fontSize:'0.85rem' },
    muted:       { fontSize:'0.85rem', color:colors.textMuted, padding:'12px 0', margin:0 },
};
